import React, {useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PaletteIcon from "@mui/icons-material/Palette";
import AddIcon from "@mui/icons-material/Add";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
    buildCustomTextureRecord,
    clampTexturePreviewZoom,
    clampTextureScaleMm,
    DEFAULT_TEXTURE_IMAGE_ADJUST,
    DEFAULT_TEXTURE_SCALE_MM,
    renderAdjustedTextureUrl,
    wrapTextureRotationDeg,
} from "./detailTextures";

/** Разрешение живого превью на сцене (баланс скорость / качество). */
const LIVE_PREVIEW_SIDE = 768;

const COLOR_SWATCHES = [
    "#c4a574",
    "#e8d4b0",
    "#8d6e63",
    "#5d4037",
    "#90a4ae",
    "#78909c",
    "#ffffff",
    "#f5f5f5",
    "#bdbdbd",
    "#616161",
    "#c62828",
    "#1565c0",
    "#2e7d32",
    "#f9a825",
];

const SV_SIZE = 160;

const sliderRowSx = {
    display: "grid",
    gridTemplateColumns: "88px 1fr 48px",
    alignItems: "center",
    gap: 1,
    mb: 0.5,
};

const labelSx = {
    fontSize: "0.85rem",
    color: "text.secondary",
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const hexToRgb = (hex) => {
    const raw = String(hex || "").replace("#", "").trim();
    if (raw.length === 3) {
        return {
            r: parseInt(raw[0] + raw[0], 16),
            g: parseInt(raw[1] + raw[1], 16),
            b: parseInt(raw[2] + raw[2], 16),
        };
    }
    if (raw.length === 6) {
        return {
            r: parseInt(raw.slice(0, 2), 16),
            g: parseInt(raw.slice(2, 4), 16),
            b: parseInt(raw.slice(4, 6), 16),
        };
    }
    return {r: 196, g: 165, b: 116};
};

const rgbToHex = (r, g, b) => {
    const to = (n) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");
    return `#${to(r)}${to(g)}${to(b)}`;
};

const normalizeHexColor = (hex) => {
    const raw = String(hex || "").replace("#", "").trim();
    if (raw.length === 3 && /^[0-9a-fA-F]{3}$/.test(raw)) {
        return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`.toLowerCase();
    }
    if (raw.length === 6 && /^[0-9a-fA-F]{6}$/.test(raw)) {
        return `#${raw.toLowerCase()}`;
    }
    return null;
};

/** HSV → RGB (для квадрата насыщенность × яркость цвета). */
const hsvToRgb = (h, s, v) => {
    const hh = ((h % 360) + 360) % 360 / 60;
    const c = v * s;
    const x = c * (1 - Math.abs((hh % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;
    if (hh < 1) {
        r = c;
        g = x;
    } else if (hh < 2) {
        r = x;
        g = c;
    } else if (hh < 3) {
        g = c;
        b = x;
    } else if (hh < 4) {
        g = x;
        b = c;
    } else if (hh < 5) {
        r = x;
        b = c;
    } else {
        r = c;
        b = x;
    }
    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
    };
};

const rgbToHsv = (r, g, b) => {
    const rr = r / 255;
    const gg = g / 255;
    const bb = b / 255;
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        switch (max) {
            case rr:
                h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
                break;
            case gg:
                h = ((bb - rr) / d + 2) / 6;
                break;
            default:
                h = ((rr - gg) / d + 4) / 6;
                break;
        }
    }
    const s = max === 0 ? 0 : d / max;
    return {h: h * 360, s, v: max};
};

/** Квадрат насыщенность × яркость выбранного цвета (без системной палитры). */
const SvColorPad = ({hue, saturation, value, onChange}) => {
    const canvasRef = useRef(null);
    const draggingRef = useRef(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const {width, height} = canvas;
        const image = ctx.createImageData(width, height);
        const data = image.data;
        for (let y = 0; y < height; y += 1) {
            const v = 1 - y / (height - 1);
            for (let x = 0; x < width; x += 1) {
                const s = x / (width - 1);
                const {r, g, b} = hsvToRgb(hue, s, v);
                const i = (y * width + x) * 4;
                data[i] = r;
                data[i + 1] = g;
                data[i + 2] = b;
                data[i + 3] = 255;
            }
        }
        ctx.putImageData(image, 0, 0);
    }, [hue]);

    const pickAt = (clientX, clientY) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
        const y = Math.min(rect.height, Math.max(0, clientY - rect.top));
        const s = x / rect.width;
        const v = 1 - y / rect.height;
        onChange?.(clamp01(s), clamp01(v));
    };

    return (
        <Box
            sx={{
                position: "relative",
                width: SV_SIZE,
                height: SV_SIZE,
                borderRadius: "6px",
                overflow: "hidden",
                border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                cursor: "crosshair",
                flexShrink: 0,
            }}
            onPointerDown={(e) => {
                draggingRef.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                pickAt(e.clientX, e.clientY);
            }}
            onPointerMove={(e) => {
                if (!draggingRef.current) return;
                pickAt(e.clientX, e.clientY);
            }}
            onPointerUp={() => {
                draggingRef.current = false;
            }}
        >
            <Box
                component="canvas"
                ref={canvasRef}
                width={SV_SIZE}
                height={SV_SIZE}
                sx={{display: "block", width: "100%", height: "100%"}}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: `${saturation * 100}%`,
                    top: `${(1 - value) * 100}%`,
                    width: 12,
                    height: 12,
                    ml: "-6px",
                    mt: "-6px",
                    borderRadius: "50%",
                    border: "2px solid #fff",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.45)",
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
};

/**
 * Модалка создания / редактирования текстуры.
 * Цвет — раскрывающаяся секция внутри (без Popover и без native color picker).
 */
const CustomTextureUploadDialog = ({
    open,
    source = null,
    onClose,
    onConfirm,
    onLivePreview,
    hasSceneTarget = false,
}) => {
    const isEdit = source?.mode === "edit";

    const [name, setName] = useState("");
    const [defaultScaleMm, setDefaultScaleMm] = useState(DEFAULT_TEXTURE_SCALE_MM);
    const [imageZoom, setImageZoom] = useState(1);
    const [imageRotationDeg, setImageRotationDeg] = useState(0);
    const [rotationInput, setRotationInput] = useState("0");
    const [brightness, setBrightness] = useState(DEFAULT_TEXTURE_IMAGE_ADJUST.brightness);
    const [contrast, setContrast] = useState(DEFAULT_TEXTURE_IMAGE_ADJUST.contrast);
    const [tintColor, setTintColor] = useState(DEFAULT_TEXTURE_IMAGE_ADJUST.tintColor);
    const [tintAmount, setTintAmount] = useState(DEFAULT_TEXTURE_IMAGE_ADJUST.tintAmount);
    const [tintHue, setTintHue] = useState(30);
    const [tintSat, setTintSat] = useState(0.45);
    const [tintVal, setTintVal] = useState(0.7);
    const [colorOpen, setColorOpen] = useState(false);
    const [customSwatches, setCustomSwatches] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const syncFromHex = (hex) => {
        const {r, g, b} = hexToRgb(hex);
        const hsv = rgbToHsv(r, g, b);
        setTintHue(Math.round(hsv.h));
        setTintSat(hsv.s);
        setTintVal(hsv.v);
    };

    const commitTintHsv = (h, s, v, {bumpAmount = true} = {}) => {
        const hue = ((h % 360) + 360) % 360;
        const sat = clamp01(s);
        const val = clamp01(v);
        setTintHue(hue);
        setTintSat(sat);
        setTintVal(val);
        const rgb = hsvToRgb(hue, sat, val);
        setTintColor(rgbToHex(rgb.r, rgb.g, rgb.b));
        if (bumpAmount && tintAmount < 0.15) setTintAmount(0.45);
    };

    const applyHue = (hueDeg) => {
        commitTintHsv(hueDeg, tintSat, tintVal);
    };

    const pickSwatch = (hex) => {
        setTintColor(hex);
        syncFromHex(hex);
        if (tintAmount < 0.15) setTintAmount(0.45);
    };

    const addCurrentColorToPalette = () => {
        const hex = normalizeHexColor(tintColor);
        if (!hex) return;
        const builtins = COLOR_SWATCHES.map((c) => c.toLowerCase());
        setCustomSwatches((prev) => {
            if (builtins.includes(hex) || prev.some((c) => c.toLowerCase() === hex)) {
                return prev;
            }
            return [...prev, hex];
        });
    };

    const paletteSwatches = useMemo(
        () => [...COLOR_SWATCHES, ...customSwatches],
        [customSwatches],
    );

    useEffect(() => {
        if (!open || !source) return undefined;
        const init = source.initial || {};
        setName(init.name || source.suggestedName || "Своя");
        setDefaultScaleMm(init.defaultScaleMm ?? DEFAULT_TEXTURE_SCALE_MM);
        const zoom = clampTexturePreviewZoom(init.imageZoom ?? init.previewZoom ?? 1);
        setImageZoom(zoom);
        const rot = wrapTextureRotationDeg(init.imageRotationDeg ?? 0);
        setImageRotationDeg(rot);
        setRotationInput(String(Math.round(rot * 10) / 10));
        setBrightness(init.brightness ?? DEFAULT_TEXTURE_IMAGE_ADJUST.brightness);
        setContrast(init.contrast ?? DEFAULT_TEXTURE_IMAGE_ADJUST.contrast);
        const nextTint = init.tintColor ?? DEFAULT_TEXTURE_IMAGE_ADJUST.tintColor;
        setTintColor(nextTint);
        const {r, g, b} = hexToRgb(nextTint);
        const hsv = rgbToHsv(r, g, b);
        setTintHue(Math.round(hsv.h));
        setTintSat(hsv.s);
        setTintVal(hsv.v);
        setTintAmount(init.tintAmount ?? DEFAULT_TEXTURE_IMAGE_ADJUST.tintAmount);
        setColorOpen(false);
        setError("");
        setBusy(false);
        return undefined;
    }, [open, source]);

    const adjust = useMemo(
        () => ({
            imageZoom,
            imageRotationDeg,
            brightness,
            contrast,
            tintColor,
            tintAmount,
        }),
        [imageZoom, imageRotationDeg, brightness, contrast, tintColor, tintAmount],
    );

    useEffect(() => {
        if (!open || !source?.sourceUrl) {
            onLivePreview?.(null);
            return undefined;
        }
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                const url = await renderAdjustedTextureUrl(source.sourceUrl, adjust, {
                    maxSide: LIVE_PREVIEW_SIDE,
                    quality: 0.85,
                });
                if (cancelled) return;
                onLivePreview?.({
                    mode: source.mode,
                    textureId: source.id,
                    url,
                    scaleMm: clampTextureScaleMm(defaultScaleMm),
                });
            } catch (err) {
                if (!cancelled) setError(err?.message || "Ошибка превью");
            }
        }, 120);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [open, source, adjust, defaultScaleMm, onLivePreview]);

    useEffect(() => {
        if (!open) onLivePreview?.(null);
    }, [open, onLivePreview]);

    const commitRotationInput = () => {
        const parsed = Number(String(rotationInput).replace(",", "."));
        const next = wrapTextureRotationDeg(Number.isFinite(parsed) ? parsed : 0);
        setImageRotationDeg(next);
        setRotationInput(String(Math.round(next * 10) / 10));
    };

    const handleConfirm = async () => {
        if (!source?.sourceUrl) return;
        setBusy(true);
        setError("");
        try {
            const url = await renderAdjustedTextureUrl(source.sourceUrl, adjust, {
                quality: 0.88,
            });
            const texture = buildCustomTextureRecord({
                id: source.id,
                name,
                url,
                sourceUrl: source.sourceUrl,
                defaultScaleMm,
                defaultRotationDeg: 0,
                previewZoom: 1,
                imageZoom,
                imageRotationDeg,
                brightness,
                contrast,
                tintColor,
                tintAmount,
            });
            onLivePreview?.(null);
            onConfirm?.(texture, {isEdit});
        } catch (err) {
            setError(err?.message || "Не удалось сохранить текстуру");
            setBusy(false);
        }
    };

    const handleClose = () => {
        if (busy) return;
        onLivePreview?.(null);
        onClose?.();
    };

    const resetColor = () => {
        setBrightness(DEFAULT_TEXTURE_IMAGE_ADJUST.brightness);
        setContrast(DEFAULT_TEXTURE_IMAGE_ADJUST.contrast);
        setTintColor(DEFAULT_TEXTURE_IMAGE_ADJUST.tintColor);
        setTintAmount(DEFAULT_TEXTURE_IMAGE_ADJUST.tintAmount);
        syncFromHex(DEFAULT_TEXTURE_IMAGE_ADJUST.tintColor);
    };

    return (
        <Dialog
            open={Boolean(open && source)}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            disableScrollLock
            disableRestoreFocus
            hideBackdrop
            sx={{
                pointerEvents: "none",
                "& .MuiDialog-container": {
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    pt: {xs: 2, md: 3},
                    pl: {xs: 1, md: 2},
                    pointerEvents: "none",
                },
                "& .MuiPaper-root": {
                    pointerEvents: "auto",
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: "10px",
                    width: "100%",
                    maxWidth: 400,
                    m: 1,
                    maxHeight: "min(92vh, 720px)",
                    position: "relative",
                },
            }}
        >
            <DialogTitle sx={{pb: 0.5, textAlign: "center", fontSize: "1.05rem"}}>
                {isEdit ? "Редактирование текстуры" : "Параметры текстуры"}
            </DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: 1.25, pt: 1}}>
                <Typography
                    className="toolbar-hint"
                    sx={{...labelSx, lineHeight: 1.4}}
                >
                    {hasSceneTarget
                        ? "Смотрите на деталь справа на сцене — там сразу видны зум, поворот, цвет и масштаб."
                        : "Выберите деталь на сцене (или назначьте эту текстуру), чтобы видеть правки вживую."}
                </Typography>

                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <IconButton
                        size="small"
                        disabled={busy || imageZoom <= 1}
                        onClick={() => setImageZoom((z) => clampTexturePreviewZoom(z - 0.1))}
                        title="Отдалить"
                    >
                        <ZoomOutIcon fontSize="small"/>
                    </IconButton>
                    <Typography sx={{...labelSx, flex: 1}}>
                        Зум картинки ×{clampTexturePreviewZoom(imageZoom).toFixed(1)}
                    </Typography>
                    <IconButton
                        size="small"
                        disabled={busy || imageZoom >= 4}
                        onClick={() => setImageZoom((z) => clampTexturePreviewZoom(z + 0.1))}
                        title="Приблизить"
                    >
                        <ZoomInIcon fontSize="small"/>
                    </IconButton>
                </Box>

                <TextField
                    label="Название"
                    size="small"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    inputProps={{maxLength: 40}}
                />

                <Box sx={sliderRowSx}>
                    <Typography sx={labelSx}>Масштаб</Typography>
                    <Slider
                        size="small"
                        min={100}
                        max={2000}
                        step={50}
                        value={clampTextureScaleMm(defaultScaleMm)}
                        onChange={(_, v) => setDefaultScaleMm(v)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v} мм`}
                    />
                    <Typography sx={{...labelSx, textAlign: "right"}}>
                        {Math.round(clampTextureScaleMm(defaultScaleMm))}
                    </Typography>
                </Box>

                <Box sx={sliderRowSx}>
                    <Typography sx={labelSx}>Зум</Typography>
                    <Slider
                        size="small"
                        min={1}
                        max={4}
                        step={0.1}
                        value={clampTexturePreviewZoom(imageZoom)}
                        onChange={(_, v) => setImageZoom(v)}
                    />
                    <Typography sx={{...labelSx, textAlign: "right"}}>
                        ×{clampTexturePreviewZoom(imageZoom).toFixed(1)}
                    </Typography>
                </Box>

                <TextField
                    label="Угол поворота, °"
                    size="small"
                    value={rotationInput}
                    onChange={(e) => setRotationInput(e.target.value)}
                    onBlur={commitRotationInput}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commitRotationInput();
                        }
                    }}
                    fullWidth
                    helperText="Любой угол, например 15 или -30"
                    inputProps={{inputMode: "decimal"}}
                />

                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <Button
                        variant={colorOpen ? "contained" : "outlined"}
                        size="small"
                        startIcon={<PaletteIcon/>}
                        onClick={() => setColorOpen((v) => !v)}
                        disabled={busy}
                    >
                        Цвет и яркость
                    </Button>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "4px",
                            border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                            backgroundColor: tintAmount > 0.01 ? tintColor : "#fff",
                            backgroundImage:
                                tintAmount < 0.01
                                    ? "linear-gradient(45deg,#ccc 25%,transparent 25%),linear-gradient(-45deg,#ccc 25%,transparent 25%)"
                                    : undefined,
                            backgroundSize: "8px 8px",
                        }}
                        title={tintAmount > 0.01 ? tintColor : "Без тонирования"}
                    />
                </Box>

                <Collapse in={colorOpen} timeout={0} unmountOnExit={false}>
                    <Box
                        sx={{
                            p: 1.25,
                            borderRadius: "8px",
                            border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                            backgroundColor: "#fff",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <Typography sx={{fontWeight: 600, fontSize: "0.9rem"}}>
                            Цвет тонирования
                        </Typography>
                        <Typography sx={{...labelSx, fontSize: "0.75rem", lineHeight: 1.35}}>
                            Образцы, квадрат + оттенок, HEX. «+» — добавить текущий цвет в палитру.
                        </Typography>
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center"}}>
                            {paletteSwatches.map((hex) => (
                                <Box
                                    key={hex}
                                    component="button"
                                    type="button"
                                    onClick={() => pickSwatch(hex)}
                                    title={hex}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        p: 0,
                                        m: 0,
                                        borderRadius: "4px",
                                        border: tintColor.toLowerCase() === hex.toLowerCase()
                                            ? "2px solid"
                                            : "1px solid",
                                        borderColor: tintColor.toLowerCase() === hex.toLowerCase()
                                            ? "primary.main"
                                            : "divider",
                                        backgroundColor: hex,
                                        cursor: "pointer",
                                    }}
                                />
                            ))}
                            <Box
                                component="button"
                                type="button"
                                onClick={addCurrentColorToPalette}
                                title="Добавить текущий цвет в палитру"
                                disabled={!normalizeHexColor(tintColor)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    p: 0,
                                    m: 0,
                                    borderRadius: "4px",
                                    border: (theme) => `1px dashed ${theme.palette.primary.stroke}`,
                                    backgroundColor: "#EEF0F5",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                    "&:disabled": {opacity: 0.5, cursor: "default"},
                                }}
                            >
                                <AddIcon sx={{fontSize: 16}}/>
                            </Box>
                        </Box>

                        <Box sx={{display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap"}}>
                            <SvColorPad
                                hue={tintHue}
                                saturation={tintSat}
                                value={tintVal}
                                onChange={(s, v) => commitTintHsv(tintHue, s, v)}
                            />
                            <Box sx={{flex: 1, minWidth: 140, display: "flex", flexDirection: "column", gap: 1}}>
                                <Box
                                    sx={{
                                        height: 36,
                                        borderRadius: "6px",
                                        border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                                        backgroundColor: tintColor,
                                    }}
                                />
                                <TextField
                                    size="small"
                                    label="HEX"
                                    value={tintColor}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        const hex = v.startsWith("#") ? v : `#${v}`;
                                        setTintColor(hex);
                                        if (/^#[0-9a-fA-F]{6}$/.test(hex) || /^#[0-9a-fA-F]{3}$/.test(hex)) {
                                            syncFromHex(hex);
                                            if (tintAmount < 0.15) setTintAmount(0.45);
                                        }
                                    }}
                                    fullWidth
                                    helperText="Например #c4a574"
                                />
                            </Box>
                        </Box>

                        <Box sx={sliderRowSx}>
                            <Typography sx={labelSx}>Оттенок</Typography>
                            <Slider
                                size="small"
                                min={0}
                                max={360}
                                step={1}
                                value={tintHue}
                                onChange={(_, v) => applyHue(v)}
                                sx={{
                                    "& .MuiSlider-rail": {
                                        opacity: 1,
                                        background:
                                            "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)",
                                    },
                                    "& .MuiSlider-track": {background: "transparent", border: "none"},
                                }}
                            />
                            <Typography sx={{...labelSx, textAlign: "right"}}>
                                {Math.round(tintHue)}°
                            </Typography>
                        </Box>

                        <Box sx={sliderRowSx}>
                            <Typography sx={labelSx}>Интенсивн.</Typography>
                            <Slider
                                size="small"
                                min={0}
                                max={1}
                                step={0.01}
                                value={tintAmount}
                                onChange={(_, v) => setTintAmount(clamp01(v))}
                            />
                            <Typography sx={{...labelSx, textAlign: "right"}}>
                                {Math.round(tintAmount * 100)}%
                            </Typography>
                        </Box>
                        <Typography sx={{...labelSx, fontSize: "0.72rem", mt: -0.5, mb: 0.5}}>
                            Интенсивность: 0% — исходная картинка, 100% — сильнее перекрасить в выбранный цвет.
                        </Typography>

                        <Typography sx={{fontWeight: 600, fontSize: "0.9rem", mt: 0.5}}>
                            Коррекция
                        </Typography>

                        <Box sx={sliderRowSx}>
                            <Typography sx={labelSx}>Яркость</Typography>
                            <Slider
                                size="small"
                                min={0.4}
                                max={1.8}
                                step={0.02}
                                value={brightness}
                                onChange={(_, v) => setBrightness(v)}
                            />
                            <Typography sx={{...labelSx, textAlign: "right"}}>
                                {brightness.toFixed(2)}
                            </Typography>
                        </Box>

                        <Box sx={sliderRowSx}>
                            <Typography sx={labelSx}>Контраст</Typography>
                            <Slider
                                size="small"
                                min={0.5}
                                max={1.8}
                                step={0.02}
                                value={contrast}
                                onChange={(_, v) => setContrast(v)}
                            />
                            <Typography sx={{...labelSx, textAlign: "right"}}>
                                {contrast.toFixed(2)}
                            </Typography>
                        </Box>

                        <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                            <Button size="small" onClick={resetColor}>Сбросить цвет</Button>
                        </Box>
                    </Box>
                </Collapse>

                {error && (
                    <Typography color="error" sx={{fontSize: "0.85rem"}}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{p: "1rem", gap: 1}}>
                <Button onClick={handleClose} disabled={busy} color="inherit">
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={busy || !name.trim()}
                >
                    {busy ? "Сохранение…" : (isEdit ? "Сохранить" : "Добавить")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomTextureUploadDialog;
